/**
 * Jobs and prices — the Time & Billing set-up a manager keeps (T&B-1 PR 1).
 *
 *   Billing jobs   the practice-wide catalogue and its free-form tasks
 *   Client prices  which billing jobs a client has, fixed price or hourly
 *   Rates and hours  each person's cost and charge-out rate, and working week
 *   Timesheets     when a week's timesheet is due, how the timer rounds
 *
 * Managers only (decision 5: rand figures are for managers). The page decides
 * nothing: every rule — steps of 0.05 hours, two decimals of rand, a leave
 * job is never chargeable, nothing is edited in place — is the server's, and
 * its messages are shown as they come back. A change to a rate, a price or
 * a setting is a NEW row from a date; the old one stays in the history.
 *
 * "Billing jobs", not "Jobs": the due list's ad-hoc Job is a different thing
 * (Wimpie, 2026-10-25).
 */

import { useCallback, useEffect, useState } from 'react'
import {
  api,
  type AuthState,
  type BillingJob,
  type ClientJobsResponse,
  type ClientListRow,
  type ObligationTypeInUse,
  type PersonTimeSetup,
  type PriceBasis,
  type PriceFrequency,
  type TimeSettingsResponse,
} from '../api'
import { AppShell } from '../components/AppShell'
import { ErrorState, LoadingState } from '../components/States'
import { typeLabel } from '../due-list'

type Tab = 'jobs' | 'prices' | 'people' | 'timesheets'
const TABS: { id: Tab; label: string }[] = [
  { id: 'jobs', label: 'Billing jobs' },
  { id: 'prices', label: 'Client prices' },
  { id: 'people', label: 'Rates and hours' },
  { id: 'timesheets', label: 'Timesheets' },
]

const FREQUENCY_WORDS: Record<PriceFrequency, string> = {
  monthly: 'per month',
  bi_monthly: 'per two months',
  annual: 'per year',
  once_off: 'once-off',
}
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const sectionCls = 'mb-4 rounded-eq-md border border-eq-border bg-white px-5 py-4'
const h2Cls = 'mb-2 font-heading text-[11px] font-bold uppercase tracking-[.08em] text-eq-neutral'
const thCls = 'px-2 py-1 text-left font-semibold'
const tdCls = 'px-2 py-1.5 align-top'
const inputCls = 'rounded-eq-sm border border-eq-border px-2 py-1 text-[13.5px]'
const labelCls = 'flex flex-col gap-0.5 text-[12px] text-eq-neutral'
const primaryBtn = (disabled: boolean) =>
  [
    'rounded-eq-sm px-3 py-1.5 font-heading text-[12.5px] font-bold',
    disabled ? 'cursor-not-allowed bg-eq-grey text-eq-helper' : 'bg-eq-navy text-white',
  ].join(' ')
const quietBtn = 'rounded-eq-sm border border-eq-navy px-3 py-1 font-heading text-[12px] font-bold text-eq-navy'

/** Integer cents as "R 1 250.00". */
const rand = (cents: number | null): string => {
  if (cents === null) return '—'
  const whole = Math.floor(cents / 100)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
  return `R ${whole}.${String(cents % 100).padStart(2, '0')}`
}
const date = (iso: string | null): string =>
  iso === null
    ? '—'
    : new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })

type Act = (fn: () => Promise<unknown>, done: string) => Promise<boolean>

export function JobsAndPricesPage({
  obligationTypes,
  auth,
}: {
  obligationTypes: ObligationTypeInUse[]
  auth: AuthState
}) {
  const [tab, setTab] = useState<Tab>('jobs')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [version, setVersion] = useState(0)

  /** Every change: run it, show the server's words on failure, then reload. */
  const act: Act = async (fn, done) => {
    if (busy) return false
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      await fn()
      setNotice(done)
      return true
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      return false
    } finally {
      setVersion((v) => v + 1)
      setBusy(false)
    }
  }

  const isManager = auth.mode === 'anonymous' || auth.user?.role === 'manager'

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Time & Billing"
      title="Jobs and prices"
      subtitle="What time is booked to, what it is charged at, and when timesheets are due. Every change is kept, from the date it applies."
    >
      {!isManager ? (
        <ErrorState message="Only a manager may see rates and prices." />
      ) : (
        <>
          <div className="mb-4 flex gap-1 border-b border-eq-border" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => {
                  setTab(t.id)
                  setError(null)
                  setNotice(null)
                }}
                className={[
                  '-mb-px border-b-2 px-3 py-2 font-heading text-[13px] font-bold',
                  tab === t.id ? 'border-eq-navy text-eq-navy' : 'border-transparent text-eq-neutral hover:text-eq-navy',
                ].join(' ')}
              >
                {t.label}
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-4">
              <ErrorState message={error} />
            </div>
          )}
          {notice && (
            <div className="mb-4 rounded-eq-md border border-eq-border bg-white px-4 py-2 text-[13.5px]">{notice}</div>
          )}

          {tab === 'jobs' && <JobsTab act={act} busy={busy} version={version} obligationTypes={obligationTypes} />}
          {tab === 'prices' && <PricesTab act={act} busy={busy} version={version} />}
          {tab === 'people' && <PeopleTab act={act} busy={busy} version={version} />}
          {tab === 'timesheets' && <TimesheetsTab act={act} busy={busy} version={version} />}
        </>
      )}
    </AppShell>
  )
}

interface TabProps {
  act: Act
  busy: boolean
  version: number
}

/** Load something, again whenever `version` moves (after every change). */
function useLoad<T>(load: () => Promise<T>, version: number, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const run = useCallback(load, deps)
  useEffect(() => {
    let live = true
    run()
      .then((d) => live && (setData(d), setError(null)))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      live = false
    }
  }, [run, version])
  return { data, error }
}

// ---------------------------------------------------------------------------
// Billing jobs
// ---------------------------------------------------------------------------

function JobsTab({ act, busy, version, obligationTypes }: TabProps & { obligationTypes: ObligationTypeInUse[] }) {
  const { data, error } = useLoad(() => api.billingJobs(), version)
  const [name, setName] = useState('')
  const [kind, setKind] = useState<'chargeable' | 'not_chargeable' | 'leave'>('chargeable')
  const [code, setCode] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  if (error) return <ErrorState message={error} />
  if (data === null) return <LoadingState what="billing jobs" />

  const jobs = data.jobs.filter((j) => showArchived || !j.archived)

  return (
    <>
      <section className={sectionCls}>
        <h2 className={h2Cls}>Add a billing job</h2>
        <p className="mb-3 mt-0 text-[12.5px] text-eq-helper">
          A billing job is what time is booked to, for every client: Monthly VAT Processing, Monthly Payroll Processing,
          Advisory, Admin. A leave job is for leave: it is never chargeable and reduces a person's available hours.
          Whether a job is chargeable is fixed once it is created.
        </p>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            void act(
              () =>
                api.createBillingJob({
                  name,
                  kind: kind === 'leave' ? 'leave' : 'work',
                  chargeable: kind === 'leave' ? undefined : kind === 'chargeable',
                  obligationTypeCode: kind === 'leave' || code === '' ? null : code,
                }),
              `Added “${name.trim()}”.`
            ).then((ok) => ok && (setName(''), setCode('')))
          }}
        >
          <label className={labelCls}>
            Name
            <input className={`${inputCls} w-72`} value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
          </label>
          <label className={labelCls}>
            Kind
            <select className={inputCls} value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
              <option value="chargeable">Work — chargeable</option>
              <option value="not_chargeable">Work — not chargeable</option>
              <option value="leave">Leave</option>
            </select>
          </label>
          {kind !== 'leave' && (
            <label className={labelCls}>
              Linked obligation type (optional)
              <select className={inputCls} value={code} onChange={(e) => setCode(e.target.value)}>
                <option value="">None</option>
                {obligationTypes.map((t) => (
                  <option key={t.code} value={t.code}>
                    {typeLabel(t.description)} ({t.code})
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="submit" className={primaryBtn(busy || name.trim() === '')} disabled={busy || name.trim() === ''}>
            Add billing job
          </button>
        </form>
      </section>

      <section className={sectionCls}>
        <div className="mb-2 flex items-center justify-between">
          <h2 className={`${h2Cls} mb-0`}>Billing jobs</h2>
          <label className="flex items-center gap-1.5 text-[12.5px] text-eq-neutral">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
            Show archived
          </label>
        </div>
        {jobs.length === 0 ? (
          <p className="m-0 text-[14px] text-eq-neutral">No billing jobs yet. Add the first one above.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {jobs.map((job) => (
              <JobCard key={job.id} job={job} act={act} busy={busy} obligationTypes={obligationTypes} />
            ))}
          </div>
        )}
      </section>
    </>
  )
}

function JobCard({
  job,
  act,
  busy,
  obligationTypes,
}: {
  job: BillingJob
  act: Act
  busy: boolean
  obligationTypes: ObligationTypeInUse[]
}) {
  const [task, setTask] = useState('')
  const [renaming, setRenaming] = useState<string | null>(null)
  const kindWords = job.kind === 'leave' ? 'Leave' : job.chargeable ? 'Chargeable' : 'Not chargeable'
  const linked = obligationTypes.find((t) => t.code === job.obligationTypeCode)

  return (
    <div className={['rounded-eq-sm border border-eq-border px-4 py-3', job.archived ? 'bg-eq-grey' : ''].join(' ')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          {renaming === null ? (
            <div className="text-[15px] font-semibold">{job.name}</div>
          ) : (
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                void act(() => api.updateBillingJob(job.id, { name: renaming }), 'Renamed.').then((ok) => ok && setRenaming(null))
              }}
            >
              <input className={inputCls} value={renaming} onChange={(e) => setRenaming(e.target.value)} aria-label="New name" />
              <button className={quietBtn} type="submit" disabled={busy}>
                Save
              </button>
              <button className="text-[12.5px] underline" type="button" onClick={() => setRenaming(null)}>
                Cancel
              </button>
            </form>
          )}
          <div className="mt-0.5 text-[12.5px] text-eq-helper">
            {kindWords}
            {job.obligationTypeCode && ` · linked to ${linked ? typeLabel(linked.description) : job.obligationTypeCode}`}
            {job.archived && ' · archived'}
          </div>
        </div>
        {!job.archived && (
          <div className="flex gap-2">
            {renaming === null && (
              <button className={quietBtn} onClick={() => setRenaming(job.name)} disabled={busy}>
                Rename
              </button>
            )}
            <button
              className={quietBtn}
              disabled={busy}
              onClick={() => {
                if (window.confirm(`Archive “${job.name}”? Nobody can book new time to it, and it cannot be undone.`)) {
                  void act(() => api.archiveBillingJob(job.id), `Archived “${job.name}”.`)
                }
              }}
            >
              Archive
            </button>
          </div>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className="text-[12px] text-eq-neutral">Tasks:</span>
        {job.tasks.length === 0 && <span className="text-[12.5px] text-eq-helper">none — time can be booked to the job alone</span>}
        {job.tasks.map((t) => (
          <span
            key={t.id}
            className={[
              'inline-flex items-center gap-1 rounded-eq-pill border border-eq-border px-2 py-0.5 text-[12.5px]',
              t.archived ? 'text-eq-helper line-through' : '',
            ].join(' ')}
          >
            {t.name}
            {!t.archived && !job.archived && (
              <button
                className="text-eq-helper hover:text-eq-danger"
                aria-label={`Archive task ${t.name}`}
                title="Archive this task"
                disabled={busy}
                onClick={() => void act(() => api.archiveBillingTask(t.id), `Archived the task “${t.name}”.`)}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {!job.archived && (
          <form
            className="flex gap-1"
            onSubmit={(e) => {
              e.preventDefault()
              void act(() => api.addBillingTask(job.id, task), `Added the task “${task.trim()}”.`).then((ok) => ok && setTask(''))
            }}
          >
            <input
              className={`${inputCls} w-32 py-0.5 text-[12.5px]`}
              placeholder="Jan2026, Review…"
              value={task}
              onChange={(e) => setTask(e.target.value)}
              aria-label={`New task for ${job.name}`}
            />
            <button className={quietBtn} type="submit" disabled={busy || task.trim() === ''}>
              Add task
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Client prices
// ---------------------------------------------------------------------------

function PricesTab({ act, busy, version }: TabProps) {
  const [search, setSearch] = useState('')
  const [clients, setClients] = useState<ClientListRow[]>([])
  const [clientId, setClientId] = useState<string | null>(null)
  const jobs = useLoad(() => api.billingJobs(), version)

  useEffect(() => {
    let live = true
    const timer = setTimeout(() => {
      api
        .clients({ q: search || null, status: 'active' })
        .then((r) => live && setClients(r.clients.slice(0, 50)))
        .catch(() => live && setClients([]))
    }, 200)
    return () => {
      live = false
      clearTimeout(timer)
    }
  }, [search])

  return (
    <>
      <section className={sectionCls}>
        <h2 className={h2Cls}>Choose a client</h2>
        <div className="flex flex-wrap items-end gap-3">
          <label className={labelCls}>
            Search
            <input className={`${inputCls} w-64`} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or number" />
          </label>
          <label className={labelCls}>
            Client
            <select className={`${inputCls} w-80`} value={clientId ?? ''} onChange={(e) => setClientId(e.target.value || null)}>
              <option value="">—</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.clientNumber} · {c.shortName}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="mb-0 mt-2 text-[12.5px] text-eq-helper">
          A fixed price is billed whatever the time took; time above it is written off. An hourly job is billed at each
          person's charge-out rate. Time on a job a client has no price for is shown as hourly work with no agreed price.
        </p>
      </section>

      {clientId !== null && jobs.data !== null && (
        <ClientPrices key={clientId} clientId={clientId} jobs={jobs.data.jobs} act={act} busy={busy} version={version} />
      )}
    </>
  )
}

function ClientPrices({
  clientId,
  jobs,
  act,
  busy,
  version,
}: TabProps & { clientId: string; jobs: BillingJob[] }) {
  const { data, error } = useLoad<ClientJobsResponse>(() => api.clientBillingJobs(clientId), version, [clientId])
  const [jobId, setJobId] = useState('')
  const [basis, setBasis] = useState<PriceBasis>('fixed')
  const [price, setPrice] = useState('')
  const [frequency, setFrequency] = useState<PriceFrequency>('monthly')
  const [from, setFrom] = useState('')
  const [note, setNote] = useState('')
  const [ending, setEnding] = useState<{ id: string; to: string } | null>(null)

  if (error) return <ErrorState message={error} />
  if (data === null) return <LoadingState what="prices" />

  const usable = jobs.filter((j) => !j.archived && j.kind === 'work')
  const ready = jobId !== '' && from !== '' && (basis === 'hourly' || price.trim() !== '')

  return (
    <>
      <section className={sectionCls}>
        <h2 className={h2Cls}>
          {data.client.clientNumber} · {data.client.shortName} — billing jobs
        </h2>
        {data.arrangements.length === 0 ? (
          <p className="m-0 text-[14px] text-eq-neutral">No billing jobs for this client yet.</p>
        ) : (
          <table className="w-full border-collapse text-[13.5px]">
            <thead className="text-[12px] text-eq-neutral">
              <tr>
                <th className={thCls}>Billing job</th>
                <th className={thCls}>Today</th>
                <th className={thCls}>From</th>
                <th className={thCls}>Next change</th>
                <th className={thCls}></th>
              </tr>
            </thead>
            <tbody>
              {data.arrangements.map((a) => {
                const now = a.today
                return (
                  <tr key={a.job.id} className="border-t border-eq-border">
                    <td className={tdCls}>
                      {a.job.name}
                      {a.job.archived && <span className="text-eq-helper"> (archived)</span>}
                      <details className="mt-1 text-[12px] text-eq-helper">
                        <summary className="cursor-pointer">History ({a.history.length})</summary>
                        <ul className="m-0 mt-1 list-none p-0">
                          {a.history.map((h) => (
                            <li key={h.id} className={h.superseded ? 'line-through' : ''}>
                              {date(h.effectiveFrom)}
                              {h.effectiveTo && ` – ${date(h.effectiveTo)}`}:{' '}
                              {h.basis === 'fixed' ? `${rand(h.priceCents)} ${FREQUENCY_WORDS[h.frequency!]}` : 'hourly'}
                              {h.note && ` — ${h.note}`}
                            </li>
                          ))}
                        </ul>
                      </details>
                    </td>
                    <td className={tdCls}>
                      {now === null
                        ? <span className="text-eq-helper">not in force</span>
                        : now.basis === 'fixed'
                          ? `${rand(now.priceCents)} ${FREQUENCY_WORDS[now.frequency!]}`
                          : 'Hourly'}
                    </td>
                    <td className={tdCls}>
                      {now === null ? '—' : date(now.effectiveFrom)}
                      {now?.effectiveTo && <div className="text-[12px] text-eq-helper">ends {date(now.effectiveTo)}</div>}
                    </td>
                    <td className={tdCls}>
                      {a.upcoming === null
                        ? '—'
                        : `${a.upcoming.basis === 'fixed' ? rand(a.upcoming.priceCents) : 'Hourly'} from ${date(a.upcoming.effectiveFrom)}`}
                    </td>
                    <td className={`${tdCls} text-right`}>
                      {now !== null && now.effectiveTo === null && (
                        ending?.id === now.id ? (
                          <form
                            className="flex justify-end gap-1"
                            onSubmit={(e) => {
                              e.preventDefault()
                              void act(() => api.endClientJob(now.id, ending.to), `${a.job.name} ends ${date(ending.to)}.`).then(
                                (ok) => ok && setEnding(null)
                              )
                            }}
                          >
                            <input
                              type="date"
                              className={inputCls}
                              value={ending.to}
                              onChange={(e) => setEnding({ id: now.id, to: e.target.value })}
                              aria-label="Last day it applies"
                            />
                            <button className={quietBtn} type="submit" disabled={busy || ending.to === ''}>
                              End
                            </button>
                          </form>
                        ) : (
                          <button className={quietBtn} onClick={() => setEnding({ id: now.id, to: '' })} disabled={busy}>
                            End…
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className={sectionCls}>
        <h2 className={h2Cls}>Add a billing job, or change a price from a date</h2>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            void act(
              () =>
                api.recordClientJob(clientId, {
                  jobId,
                  basis,
                  price: basis === 'fixed' ? price : undefined,
                  frequency: basis === 'fixed' ? frequency : undefined,
                  effectiveFrom: from,
                  note: note || undefined,
                }),
              'Saved. The earlier price stays in the history.'
            ).then((ok) => ok && (setPrice(''), setNote('')))
          }}
        >
          <label className={labelCls}>
            Billing job
            <select className={inputCls} value={jobId} onChange={(e) => setJobId(e.target.value)}>
              <option value="">—</option>
              {usable.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.name}
                </option>
              ))}
            </select>
          </label>
          <label className={labelCls}>
            Basis
            <select className={inputCls} value={basis} onChange={(e) => setBasis(e.target.value as PriceBasis)}>
              <option value="fixed">Fixed price</option>
              <option value="hourly">Hourly</option>
            </select>
          </label>
          {basis === 'fixed' && (
            <>
              <label className={labelCls}>
                Price excl. VAT (R)
                <input className={`${inputCls} w-28 text-right`} value={price} onChange={(e) => setPrice(e.target.value)} inputMode="decimal" />
              </label>
              <label className={labelCls}>
                Charged
                <select className={inputCls} value={frequency} onChange={(e) => setFrequency(e.target.value as PriceFrequency)}>
                  {(Object.keys(FREQUENCY_WORDS) as PriceFrequency[]).map((f) => (
                    <option key={f} value={f}>
                      {FREQUENCY_WORDS[f]}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
          <label className={labelCls}>
            From
            <input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className={labelCls}>
            Note (optional)
            <input className={`${inputCls} w-56`} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button type="submit" className={primaryBtn(busy || !ready)} disabled={busy || !ready}>
            Save
          </button>
        </form>
      </section>
    </>
  )
}

// ---------------------------------------------------------------------------
// Rates and hours
// ---------------------------------------------------------------------------

function PeopleTab({ act, busy, version }: TabProps) {
  const { data, error } = useLoad(() => api.timePeople(), version)
  const [open, setOpen] = useState<{ userId: string; what: 'rate' | 'hours' } | null>(null)

  if (error) return <ErrorState message={error} />
  if (data === null) return <LoadingState what="rates and hours" />

  return (
    <section className={sectionCls}>
      <h2 className={h2Cls}>Everyone with access to EQ Practice</h2>
      <p className="mb-3 mt-0 text-[12.5px] text-eq-helper">
        Rates are per hour, excluding VAT. Time is valued at the rate in force on the day the work was done. A new rate or
        working week applies from its date; to correct a mistake, record the right figure for the same date.
      </p>
      <table className="w-full border-collapse text-[13.5px]">
        <thead className="text-[12px] text-eq-neutral">
          <tr>
            <th className={thCls}>Person</th>
            <th className={thCls}>Charge-out / hour</th>
            <th className={thCls}>Cost / hour</th>
            <th className={thCls}>Working week</th>
            <th className={thCls}></th>
          </tr>
        </thead>
        <tbody>
          {data.people.map((p) => (
            <PersonRow
              key={p.person.id}
              setup={p}
              open={open?.userId === p.person.id ? open.what : null}
              setOpen={(what) => setOpen(what === null ? null : { userId: p.person.id, what })}
              act={act}
              busy={busy}
              today={data.today}
            />
          ))}
        </tbody>
      </table>
    </section>
  )
}

function PersonRow({
  setup,
  open,
  setOpen,
  act,
  busy,
  today,
}: {
  setup: PersonTimeSetup
  open: 'rate' | 'hours' | null
  setOpen: (what: 'rate' | 'hours' | null) => void
  act: Act
  busy: boolean
  today: string
}) {
  const { person, rateToday, patternToday } = setup
  const [from, setFrom] = useState(today)
  const [cost, setCost] = useState('')
  const [charge, setCharge] = useState('')
  const [hours, setHours] = useState<string[]>(patternToday?.hours ?? ['8', '8', '8', '8', '8', '0', '0'])
  const [note, setNote] = useState('')

  return (
    <>
      <tr className="border-t border-eq-border">
        <td className={tdCls}>
          {person.name}
          <div className="text-[12px] text-eq-helper">{person.role === 'manager' ? 'Manager' : 'Staff'}</div>
        </td>
        <td className={`${tdCls} tabular-nums`}>
          {rateToday ? rand(rateToday.chargeOutRateCents) : <span className="text-eq-helper">not set</span>}
        </td>
        <td className={`${tdCls} tabular-nums`}>{rateToday ? rand(rateToday.costRateCents) : '—'}</td>
        <td className={tdCls}>
          {patternToday ? (
            <>
              {patternToday.weeklyHours} h
              <div className="text-[12px] text-eq-helper">
                {patternToday.hours.map((h, i) => `${DAY_SHORT[i]} ${h}`).join(' · ')}
              </div>
            </>
          ) : (
            <span className="text-eq-helper">not set</span>
          )}
        </td>
        <td className={`${tdCls} text-right`}>
          <span className="inline-flex gap-2">
            <button className={quietBtn} onClick={() => setOpen(open === 'rate' ? null : 'rate')} disabled={busy}>
              New rate…
            </button>
            <button className={quietBtn} onClick={() => setOpen(open === 'hours' ? null : 'hours')} disabled={busy}>
              New week…
            </button>
          </span>
        </td>
      </tr>
      {open !== null && (
        <tr>
          <td colSpan={5} className="bg-eq-grey px-3 py-3">
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault()
                const run =
                  open === 'rate'
                    ? () => api.recordRate({ userId: person.id, effectiveFrom: from, costRate: cost, chargeOutRate: charge, note: note || undefined })
                    : () => api.recordWorkPattern({ userId: person.id, effectiveFrom: from, hours, note: note || undefined })
                void act(run, open === 'rate' ? `Recorded ${person.name}'s rate from ${date(from)}.` : `Recorded ${person.name}'s working week from ${date(from)}.`).then(
                  (ok) => ok && (setOpen(null), setNote(''))
                )
              }}
            >
              <label className={labelCls}>
                From
                <input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} />
              </label>
              {open === 'rate' ? (
                <>
                  <label className={labelCls}>
                    Charge-out / hour (R)
                    <input className={`${inputCls} w-28 text-right`} value={charge} onChange={(e) => setCharge(e.target.value)} inputMode="decimal" />
                  </label>
                  <label className={labelCls}>
                    Cost / hour (R)
                    <input className={`${inputCls} w-28 text-right`} value={cost} onChange={(e) => setCost(e.target.value)} inputMode="decimal" />
                  </label>
                </>
              ) : (
                hours.map((h, i) => (
                  <label key={i} className={labelCls}>
                    {DAY_SHORT[i]}
                    <input
                      className={`${inputCls} w-14 text-right`}
                      value={h}
                      inputMode="decimal"
                      aria-label={`${WEEKDAYS[i]} hours`}
                      onChange={(e) => setHours(hours.map((x, j) => (j === i ? e.target.value : x)))}
                    />
                  </label>
                ))
              )}
              <label className={labelCls}>
                Note (optional)
                <input className={`${inputCls} w-48`} value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
              <button
                type="submit"
                className={primaryBtn(busy || from === '' || (open === 'rate' && (cost === '' || charge === '')))}
                disabled={busy || from === '' || (open === 'rate' && (cost === '' || charge === ''))}
              >
                Save
              </button>
            </form>
            {(open === 'rate' ? setup.rates.length : setup.patterns.length) > 0 && (
              <ul className="m-0 mt-3 list-none p-0 text-[12px] text-eq-helper">
                {open === 'rate'
                  ? setup.rates.map((r) => (
                      <li key={r.id} className={r.superseded ? 'line-through' : ''}>
                        From {date(r.effectiveFrom)}: charge-out {rand(r.chargeOutRateCents)}, cost {rand(r.costRateCents)}
                        {r.note && ` — ${r.note}`}
                      </li>
                    ))
                  : setup.patterns.map((p) => (
                      <li key={p.id} className={p.superseded ? 'line-through' : ''}>
                        From {date(p.effectiveFrom)}: {p.weeklyHours} h ({p.hours.join(' / ')}){p.note && ` — ${p.note}`}
                      </li>
                    ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Timesheets
// ---------------------------------------------------------------------------

function TimesheetsTab({ act, busy, version }: TabProps) {
  const { data, error } = useLoad<TimeSettingsResponse>(() => api.timeSettings(), version)
  const [weekday, setWeekday] = useState<number | null>(null)
  const [time, setTime] = useState<string | null>(null)
  const [rounding, setRounding] = useState<number | null>(null)
  const [note, setNote] = useState('')

  if (error) return <ErrorState message={error} />
  if (data === null) return <LoadingState what="timesheet settings" />
  if (data.current === null) {
    return <ErrorState message="This practice has no timesheet setting. The T&B-1 migration creates one; ask for it to be checked." />
  }
  const cur = data.current
  const wd = weekday ?? cur.dueWeekday
  const tm = time ?? cur.dueTime
  const rd = rounding ?? cur.timerRoundingHundredths
  const changed = wd !== cur.dueWeekday || tm !== cur.dueTime || rd !== cur.timerRoundingHundredths

  return (
    <>
      <section className={sectionCls}>
        <h2 className={h2Cls}>When a week's timesheet is due</h2>
        <p className="mb-3 mt-0 text-[14px]">
          Now: <b>{WEEKDAYS[cur.dueWeekday - 1]} {cur.dueTime}</b> for the week before. The timer rounds up to the next{' '}
          <b>{(cur.timerRoundingHundredths / 100).toString()} hour</b>. A late week gets a reminder and appears in the
          late-timesheet log; nothing is blocked.
        </p>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            void act(
              () => api.recordTimeSettings({ dueWeekday: wd, dueTime: tm, timerRoundingHundredths: rd, note: note || undefined }),
              'Saved. Weeks already due keep the time they were due by.'
            ).then((ok) => ok && (setWeekday(null), setTime(null), setRounding(null), setNote('')))
          }}
        >
          <label className={labelCls}>
            Due day
            <select className={inputCls} value={wd} onChange={(e) => setWeekday(Number(e.target.value))}>
              {WEEKDAYS.map((d, i) => (
                <option key={d} value={i + 1}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <label className={labelCls}>
            Due time
            <input type="time" className={inputCls} value={tm} onChange={(e) => setTime(e.target.value)} />
          </label>
          <label className={labelCls}>
            Timer rounds up to
            <select className={inputCls} value={rd} onChange={(e) => setRounding(Number(e.target.value))}>
              <option value={5}>0.05 h (3 min)</option>
              <option value={10}>0.1 h (6 min)</option>
              <option value={25}>0.25 h (15 min)</option>
            </select>
          </label>
          <label className={labelCls}>
            Note (optional)
            <input className={`${inputCls} w-56`} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button type="submit" className={primaryBtn(busy || !changed)} disabled={busy || !changed}>
            Save
          </button>
        </form>
      </section>
      <section className={sectionCls}>
        <h2 className={h2Cls}>History</h2>
        <ul className="m-0 list-none p-0 text-[13px]">
          {data.history.map((h) => (
            <li key={h.id} className="py-0.5">
              {new Date(h.effectiveAt).toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}:{' '}
              {WEEKDAYS[h.dueWeekday - 1]} {h.dueTime}, timer {h.timerRoundingHundredths / 100} h
              {h.note && <span className="text-eq-helper"> — {h.note}</span>}
            </li>
          ))}
        </ul>
      </section>
    </>
  )
}
